
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import List from '@/common/components/UI/List/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// module components
import MicroservicesListItem from '@/modules/GatewayModule/components/MicroservicesListItem/index.vue'
import MicroservicesActions from '@/modules/GatewayModule/components/MicroservicesActions/index.vue'
import MicroservicesFilters from '@/modules/GatewayModule/components/MicroservicesFilters/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import {
    CreateMicroservicePayloadT,
    GatewayMicroserviceT,
    MicroservicesListParamsT,
} from '@/core/types/gateway.types'

// realisations
import { gateway } from '@/core/realisations/gateway.realisation'

// composable
import useActions from '@/common/composable/useActions'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        list: List,
        'failed-fetch': FailedFetch,
        'the-button': TheButton,
        'microservices-list-item': MicroservicesListItem,
        'microservices-actions': MicroservicesActions,
        'microservices-filters': MicroservicesFilters,
    },
    setup() {
        const state = reactive({
            is_importing: false,
            microservices: new DataState<GatewayMicroserviceT>({}),
        })

        const filter_state: MicroservicesListParamsT = reactive({
            name: undefined as string | undefined,
            isActive: 0 as number,
        })

        const loadMicroservices = async (page: number) => {
            state.microservices.pagination.current_page = page

            try {
                state.microservices.is_loading = true

                const response = await gateway.getMicroservices(page, '', filter_state)
                state.microservices.list = response.data
                state.microservices.pagination.total_pages = response.meta.last_page

                state.microservices.is_failed = false
                state.microservices.failure.error_state.is_cancel = false
            } catch (error) {
                state.microservices.failure.catchError(error)

                if (!state.microservices.failure.error_state.is_cancel) {
                    state.microservices.is_failed = true
                }
            } finally {
                if (!state.microservices.failure.error_state.is_cancel) {
                    state.microservices.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadMicroservices(1)
        })

        const importAllRoutes = async () => {
            const answer = confirm('Вы действительно хотите импортировать все routes?')
            if (!answer) return

            try {
                state.is_importing = true
                await gateway.importAllRoutes()
            } catch (error) {
                console.error(error)
            } finally {
                state.is_importing = false
            }
        }

        const { actions_state, startCreate, startUpdate } = useActions<GatewayMicroserviceT>()

        const createMicroservice = async (microservice: CreateMicroservicePayloadT) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.createMicroservice(microservice)
                await loadMicroservices(state.microservices.pagination.current_page)
                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateMicroservice = async (
            microservice: CreateMicroservicePayloadT & { id: number }
        ) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.updateMicroservice(microservice.id, microservice)
                await loadMicroservices(state.microservices.pagination.current_page)
                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onSearch = (role: MicroservicesListParamsT) => {
            filter_state.name = role.name
            filter_state.isActive = role.isActive

            loadMicroservices(1)
        }

        const onReset = () => {
            filter_state.name = undefined
            filter_state.isActive = 0

            loadMicroservices(1)
        }

        return {
            state,
            loadMicroservices,
            importAllRoutes,
            actions_state,
            startCreate,
            startUpdate,
            createMicroservice,
            updateMicroservice,
            // filters
            onSearch,
            onReset,
        }
    },
})
